/**
 * How a screen gets back to the truth after the socket failed to deliver it.
 *
 * The failure, reported from a live venue: the guest puts the phone down or
 * switches app, the kitchen moves the order PREPARING → READY → SERVED, and
 * when they look again the screen still says PREPARING. It stays wrong until
 * they reload the page by hand. Nothing was broken on the server — every event
 * fired — but a backgrounded mobile browser throttles its timers, freezes the
 * page and drops or suspends the websocket underneath it, and Socket.IO replays
 * room membership on reconnect and never the events emitted while it was away.
 * A screen whose only source of truth is a push therefore has no route back to
 * the truth at all: it is not stale for a while, it is stale for ever.
 *
 * This is the net beneath the push, not a replacement for it. The subscriptions
 * stay exactly as they are and stay instant; what is added is three answers to
 * "the screen may have missed something", because no one of them is enough:
 *
 *  1. **The socket reconnected.** Covers the backend restart and the Wi-Fi blip
 *     the guest never noticed. It does not cover a page that was frozen and
 *     resumed with its socket still nominally up.
 *  2. **The tab came back.** Covers exactly that case, and it is the one the
 *     bug report is about. It does not cover a guest who never leaves the page.
 *  3. **A slow poll.** Covers the case neither of the other two can see: a
 *     socket that is *connected and silently useless* — a proxy that dropped
 *     the upgrade, a radio that came back without the connection under it. No
 *     `disconnect` fires, no `visibilitychange` fires, and the screen would sit
 *     green and wrong indefinitely. It is deliberately slow, and it stops while
 *     the tab is hidden: a phone in a pocket must not spend an hour asking a
 *     venue's Wi-Fi a question nobody is there to read the answer to.
 *
 * One rule, one file, used by every guest surface that shows live state, so
 * "how does this app catch up" has a single answer rather than one per screen.
 */

/**
 * How long a screen with nothing pushing at it waits before asking anyway.
 *
 * A minute, and the number is chosen against a backstop's job rather than a
 * poller's. This is what bounds "wrong for ever" at "wrong for at most a
 * minute"; the socket is what makes it instant in the ordinary case, and if the
 * interval were tuned as though it were the delivery mechanism it would be
 * seconds, which is a request per guest per few seconds on venue Wi-Fi with
 * several phones sitting on every table.
 */
export const RESYNC_POLL_MS = 60_000;

/**
 * How long one catch-up swallows the next.
 *
 * Coming back to the foreground trips more than one sensor: the browser fires
 * `visibilitychange` the moment the page is resumed, and the socket — which the
 * OS suspended while the app was away — finishes its handshake a second or two
 * later and reports a reconnect. They describe the same event, so they must
 * cost one request, not two or three.
 *
 * Leading edge, not trailing: the read goes out on the *first* signal and the
 * rest of the window is silence. A trailing debounce would have to be at least
 * this long to merge the pair, and would then delay every catch-up by that long
 * — the guest would watch a stale screen for two seconds after picking the
 * phone up, which is the thing being fixed, only shorter.
 *
 * The sliver this leaves — an event emitted between the read going out and the
 * socket finishing its handshake — is what the poll above is for.
 */
export const RESYNC_WINDOW_MS = 2_000;

export interface ResyncWiring {
  /** Re-read the server. Called at most once per {@link RESYNC_WINDOW_MS}. */
  onResync: () => void;
  /**
   * Subscribe to realtime coming *back*; returns the unsubscribe.
   *
   * Passed in rather than imported so this file knows when a screen should
   * catch up and nothing at all about what a socket is — which is also what
   * lets the whole rule be exercised without a gateway to reconnect to.
   */
  onReconnect: (listener: () => void) => () => void;
  pollMs?: number;
  windowMs?: number;
}

/** Wire the three sensors to one re-read. Returns the teardown. */
export function startResync({
  onResync,
  onReconnect,
  pollMs = RESYNC_POLL_MS,
  windowMs = RESYNC_WINDOW_MS,
}: ResyncWiring): () => void {
  /** Non-null while a catch-up has just gone out and the window is still open. */
  let cooling: ReturnType<typeof setTimeout> | null = null;

  const visible = () => document.visibilityState === 'visible';

  const catchUp = () => {
    if (cooling !== null) return;
    cooling = setTimeout(() => {
      cooling = null;
    }, windowMs);
    onResync();
  };

  const onVisibility = () => {
    // Only on the way back. Going away is not a moment anything is missed at,
    // and a read fired at a page that is about to be frozen is a request whose
    // answer arrives to nobody.
    if (visible()) catchUp();
  };
  document.addEventListener('visibilitychange', onVisibility);

  const stopReconnect = onReconnect(catchUp);

  const poll = setInterval(() => {
    // A hidden tab's timers are throttled to a crawl anyway, so this is not
    // only about battery: a tick that finally fires after a browser has held it
    // back for minutes would ask on behalf of a screen nobody is looking at,
    // and the `visibilitychange` above already covers the moment they are.
    if (visible()) catchUp();
  }, pollMs);

  return () => {
    document.removeEventListener('visibilitychange', onVisibility);
    stopReconnect();
    clearInterval(poll);
    if (cooling !== null) clearTimeout(cooling);
  };
}
